(ns app.front.view.admin.gis.server-config
  (:require [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.section :as section]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.gis.common :as gis-common]
            [app.front.view.admin.gis.map-config :as gis-map-config]
            [app.front.view.admin.gis.search-config :as gis-search-config]
            [app.gis.config-model :as gis-config-model]
            [clojure.string :as str]
            [core.domain.validators :as validators]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def system-specific-keys {:cgis    {:gis-server-config/service-url             :gis-server-config/service-url-cgis
                                     :gis-server-config/service-url-placeholder :gis-server-config/service-url-placeholder-cgis}
                           :wms-wfs {:gis-server-config/service-url             :gis-server-config/service-url-wms-wfs
                                     :gis-server-config/service-url-placeholder :gis-server-config/service-url-placeholder-wms-wfs}})


(defn system-tr [tr system [key]]
  (tr [(get-in system-specific-keys [system key] key)]))


(defn config-of-type? [{:gis-server-config/keys [system]} type]
  (= type system))


(defn authentication-config-keys [config]
  (cond->> []
    (not (config-of-type? config :regrid-arcgis)) (concat [:gis-server-config/username :gis-server-config/password])
    (or (config-of-type? config :arcgis)
        (config-of-type? config :azimut-arcgis)) (concat [:gis-server-config/arcgis-token-url])
    (config-of-type? config :arcgis) (concat [:gis-server-config/arcgis-auth-type])
    (config-of-type? config :cgis) (concat [:gis-server-config/cgis-dataset-id])))


(defn all-config-keys [config] (concat
                                 [:gis-server-config/system :gis-server-config/name :gis-server-config/service-url]
                                 (authentication-config-keys config)))


(defn check-config-keys [config]
  (-> config
      all-config-keys
      set
      (disj :gis-server-config/name :gis-server-config/system)))


(defn arcgis-wo-auth? [config]
  (and (config-of-type? config :arcgis)
       (= (:gis-server-config/arcgis-auth-type config)
          :arcgis.authentication/none)))


(defn sanitize-server-config [config]
  (cond->> (select-keys config (all-config-keys config))
    :always (p/remove-vals str/blank?)
    (arcgis-wo-auth? config) (p/remove-keys #{:gis-server-config/arcgis-token-url
                                              :gis-server-config/username
                                              :gis-server-config/password})))


(rf/reg-event-fx
  ::check-gis-server-config
  (fn [{:keys [db]} [_ server-id]]
    (p/if-all-let [config      (get-in db [::server-config-editors server-id])
                   service-url (not-empty (:gis-server-config/service-url config))]
      (when (validators/valid-uri? service-url)
        {:db           (assoc-in db [::server-config-status server-id] {:loading? true})
         :comm/command {:command    :admin/check-gis-server-config
                        :data       (-> config
                                        sanitize-server-config
                                        (dissoc :gis-server-config/name))
                        :on-success [::check-gis-server-config.success server-id]}}))))


(rf/reg-event-db ::check-gis-server-config.success
  (fn [db [_ server-id _ response]]
    (assoc-in db [::server-config-status server-id] response)))


(rf/reg-event-fx ::set-server-editor-value
  (fn [{:keys [db]} [_ server-id partial-config]]
    (let [new-config          (merge (get-in db [::server-config-editors server-id])
                                     partial-config)
          partial-with-system (select-keys new-config (concat (keys partial-config)
                                                              [:gis-server-config/system]))]
      (merge {:db (assoc-in db [::server-config-editors server-id] new-config)}
             (when (and (not (gis-common/wms-wfs-server? new-config))
                        (some (check-config-keys partial-with-system) (keys partial-config)))
               {:dispatch-debounce {:id      ::check-gis-server-config
                                    :timeout 800
                                    :action  :dispatch
                                    :event   [::check-gis-server-config server-id]}})))))


(rf/reg-event-db ::close-server-config-editor
  (fn [db [_ server-id]]
    (-> (p/dissoc-in db [::server-config-editors server-id])
        (p/dissoc-in [::server-config-status server-id]))))


(rf/reg-event-fx ::save-server-config
  (fn [{:keys [db]} [_ server-id config]]
    {:db           (assoc db ::save-in-progress? true)
     :comm/command {:command          :admin/save-gis-server-config
                    :method           :post
                    :data             (merge (sanitize-server-config config)
                                             (when (not= :new server-id)
                                               {:id server-id}))
                    :notify           {:success :gis-editor/config-saved}
                    :on-success       [::save-server-config.success server-id]
                    :finally-dispatch [::save-config.done]}}))


(rf/reg-event-fx ::save-server-config.success
  (fn [_ [_ server-id status changes]]
    {:fx [[:dispatch [::close-server-config-editor server-id]]
          [:dispatch [::entities/apply-changes status changes]]]}))


(rf/reg-event-db ::save-config.done
  #(dissoc % ::save-in-progress?))


(rf/reg-sub ::save-in-progress? #(-> % ::save-in-progress? boolean))


(rf/reg-sub ::server-config-in-editor
  (fn [db [_ server-id]]
    (get-in db [::server-config-editors server-id])))


(rf/reg-sub ::server-config-status
  (fn [db [_ server-id]]
    (get-in db [::server-config-status server-id])))


(rf/reg-sub ::servers
  :<- [::entities/entities {:entity/type :entity.type/gis-server-config}]
  (fn [servers _]
    (->> servers
         (sort-by (comp str/upper-case :gis-server-config/name)))))


(rf/reg-sub ::servers-of-type
  :<- [::servers]
  :<- [::system-filter]
  (fn [[servers system]]
    (->> servers
         (filter #(or (= system :all)
                      (-> % :gis-server-config/system (= system)))))))


(rf/reg-sub ::system-filter
  #(::system-filter % :all))


(rf/reg-event-db ::set-system-filter
  (fn [db [_ system]]
    (assoc db ::system-filter system)))


(rf/reg-sub ::all-server-urls
  :<- [::servers]
  (fn [servers _]
    (->> servers
         (map :gis-server-config/service-url)
         set)))


(rf/reg-sub ::server-groups
  (fn [[_ server-id]]
    (rf/subscribe [::entities/entity server-id]))
  (fn [server _]
    (->> server
         :gis-service-config/_gis-server-config
         (mapcat (juxt :group/_gis-search-configs :group/_gis-map-configs))
         (apply concat)
         distinct
         (map :group/name)
         sort)))


(def server-config-checker (s/checker gis-config-model/GisServerConfig))


(defn valid-server-config? [config server-id]
  (let [keys-to-check (cond-> (-> config authentication-config-keys set)
                        (not= server-id :new) (disj :gis-server-config/password)
                        (arcgis-wo-auth? config) (disj :gis-server-config/arcgis-auth-type
                                                       :gis-server-config/arcgis-token-url
                                                       :gis-server-config/username
                                                       :gis-server-config/password))]
    (and (nil? (server-config-checker config))
         (or (every? #(str/blank? (get config %)) keys-to-check)
             (not-any? #(str/blank? (get config %)) keys-to-check)))))


(defn trim-server-url [url]
  (or (re-find #".+/rest/services"
               url)
      url))


(defn assoc-arcgis-auth-type [config]
  (if (config-of-type? config :arcgis)
    (assoc config
           :gis-server-config/arcgis-auth-type (gis-config-model/resolve-arcgis-auth-type config))
    config))


(defn gis-server-editor [tr server-id]
  (let [config                                             (<sub [::server-config-in-editor server-id])
        {:keys [status message folders services loading?]} (<sub [::server-config-status server-id])
        status-ok?                                         (if (gis-common/wms-wfs-server? config)
                                                             ;; For WMS/WFS server, status is not checked at this point
                                                             true
                                                             (= status :ok))
        invalid-config?                                    (or (not (valid-server-config? config server-id))
                                                               (not status-ok?))
        other-server-urls                                  (<sub [::all-server-urls])
        duplicate?                                         (and (= server-id :new)
                                                                (contains? other-server-urls (:gis-server-config/service-url config)))
        of-type?                                           (partial config-of-type? config)
        tr                                                 (partial system-tr tr (:gis-server-config/system config))]
    [:section.bg-ui-light-gray.rounded
     [:div.flex.flex-wrap
      [:div.w-full.lg:w-1-2.lg:pr-4
       (for-frag [k (all-config-keys config)]
         (let [service-url? (= k :gis-server-config/service-url)
               hide-field?  (and (of-type? :arcgis)
                                 (= :arcgis.authentication/none (:gis-server-config/arcgis-auth-type config))
                                 (contains? (-> config authentication-config-keys set) k))
               id           (str (if (keyword? server-id) (name server-id) server-id) "-" (name k))]
           [:div.mt-4
            (cond
              (= k :gis-server-config/system)
              [ui-radio/radio
               {:name       (str "gis-type-" server-id)
                :label-text (tr [:gis-server-config/system])
                :values     gis-config-model/system-types
                :value      (:gis-server-config/system config)
                :on-change  #(>evt [::set-server-editor-value
                                    server-id
                                    {:gis-server-config/system %}])
                :val->str   #(tr [(keyword "gis-server-config" %)])
                :disabled   (not= server-id :new)}]

              (= k :gis-server-config/arcgis-auth-type)
              [ui-radio/radio
               {:name       (str "arcgis-auth-type-" server-id)
                :label-text (tr [k])
                :values     gis-config-model/arcgis-auth-types
                :value      (:gis-server-config/arcgis-auth-type config)
                :on-change  #(>evt [::set-server-editor-value
                                    server-id
                                    {:gis-server-config/arcgis-auth-type %}])
                :val->str   #(tr [%])}]

              :else
              (when-not hide-field?
                [ui/text-input {:id          id
                                :label-text  (tr [k])
                                :helper-text (cond
                                               (and (of-type? :arcgis)
                                                    service-url?)
                                               (tr [:gis-server-config/service-url-help-text])

                                               (and (of-type? :azimut-arcgis)
                                                    service-url?)
                                               (tr [:gis-server-config/azimut-service-url-help-text])

                                               (and (= k :gis-server-config/password)
                                                    (not= server-id :new))
                                               (tr [:gis-server-config/password-hidden]))
                                :light       true
                                :value       (k config)
                                :placeholder (tr [(keyword "gis-server-config" (keyword (str (name k) "-placeholder")))])
                                :on-change   #(rf/dispatch-sync
                                                [::set-server-editor-value
                                                 server-id
                                                 (merge {k (cond-> (futil/tval %)
                                                             (of-type? :arcgis) trim-server-url)}
                                                        (select-keys config [:gis-server-config/system]))])}]))
            (when (and (= k :gis-server-config/service-url)
                       duplicate?)
              [:p.pt-2.pb-0.font-bold.text-ui-red "This seems to be a duplicate of an existing config. Should you use that instead?"])]))]
      (when (not (gis-common/wms-wfs-server? config))
        [:div.w-full.lg:w-1-2.lg:pl-4.pt-6
         (when loading?
           (cc/inline-loading {:description (tr [:gis-editor/checking-server])}))
         (when status
           [:<>
            [:table
             [:tbody
              [:tr
               [:td.font-semibold.p-2 (tr [:gis-server-config/status])]
               [:td.p-2 [ui/tag {:type       :round
                                 :class-name (if (= status :ok)
                                               "bg-ui-green"
                                               "bg-ui-red-100")}
                         (-> status name str/upper-case)]]]
              [:tr
               [:td.font-semibold.p-2 (tr [:gis-server-config/message])]
               [:td.p-2 message]]
              (when (and (of-type? :arcgis) (not= status :error))
                [:tr
                 [:td.font-semibold.p-2 (tr [:gis-server-config/root-folders])]
                 [:td.p-2.font-mono
                  (if (seq folders)
                    (for-frag [folder folders]
                      [:div.mt-1 folder])
                    (str "<" (tr [:gis-server-config/no-folders]) ">"))]])
              (when (and (of-type? :arcgis) (not= status :error))
                [:tr
                 [:td.font-semibold.p-2 (tr [:gis-server-config/root-services])]
                 [:td.p-2.font-mono
                  (if (seq services)
                    (for-frag [service services]
                      [:div.mt-1 service])
                    (str "<" (tr [:gis-server-config/no-services]) ">"))]])]]
            (when (and (of-type? :arcgis) (= status :ok) (empty? folders) (empty? services))
              [:div.mt-3
               (tr [:gis-server-config/no-folders-guide])])])])]
     [:div.flex.justify-end.mt-3
      (when (not= server-id :new)
        [ui/modal-confirmation-button {:tr                  tr
                                       :trigger-button-text (tr [:remove])
                                       :trigger-button-opts {:kind       :danger
                                                             :class-name "mr-2"}
                                       :on-yes-click        #(>evt [::gis-common/remove-config :admin/remove-gis-server-config server-id])}
         (tr [:gis-editor/confirm-server-removal]
             [(:gis-server-config/name config)])])
      (cc/button {:kind       :ghost
                  :class-name "mr-2"
                  :on-click   #(>evt [::close-server-config-editor server-id])}
                 (tr [:cancel]))
      (cc/button {:disabled (or invalid-config?
                                (<sub [::save-in-progress?]))
                  :on-click #(>evt [::save-server-config server-id config])}
                 (tr [:save]))]]))


(defn system-server-list [tr]
  (let [servers (<sub [::servers-of-type])]
    [:<>
     (for-frag [{:gis-server-config/keys [name system] :as server} servers
                :let [server-id (:db/id server)
                      groups    (<sub [::server-groups server-id])]]
       (if (<sub [::server-config-in-editor server-id])
         [gis-server-editor tr server-id]
         [section/section
          (str server-id "-config-section")
          {:tr                   tr
           :class                "bg-ui-light-gray rounded"
           :initially-collapsed? true
           :heading-button       (cc/button {:size             :small
                                             :kind             :ghost
                                             :icon-description ""
                                             :on-click         #(>evt [::set-server-editor-value
                                                                       server-id
                                                                       (->> (st/required-keys gis-config-model/GisServerConfig)
                                                                            keys
                                                                            (select-keys server)
                                                                            assoc-arcgis-auth-type)])}
                                            (tr [:edit]))
           :heading-extra        [:div.flex.flex-wrap.flex-1
                                  [ui/tag {:type       :round
                                           :color      (if (gis-common/arcgis-server? server) :night-sky :blue)
                                           :class-name "normal-case"}
                                   (tr [(keyword "gis-server-config" system)])]
                                  (for-frag [group groups]
                                    [ui/tag {:type       :round
                                             :class-name "bg-ui-violet-80 text-white"}
                                     group])]}
          name
          [:div
           [:div.flex.flex-wrap.-mt-3
            (for-frag [k [:gis-server-config/service-url
                          :gis-server-config/arcgis-token-url
                          :gis-server-config/username]
                       :let [v (k server)]
                       :when (not (str/blank? v))]
              [gis-common/form-item tr k v])]
           [:div.mt-4
            [gis-search-config/gis-search-config-list tr server]
            (when (gis-common/arcgis-server? server)
              [gis-search-config/gis-row-config-list tr server])
            (when (contains? #{:arcgis :wms-wfs :azimut-arcgis} system)
              [gis-map-config/gis-map-config-list tr server])]]]))]))


(defn gis-server-list [tr]
  (let [system-filter (<sub [::system-filter])]
    [section/section
     :gis-servers
     {:tr                   tr
      :initially-collapsed? true}
     (tr [:gis-editor/servers])
     [:div.flex.justify-between.items-center
      [ui-radio/radio
       {:name       "gis-type-filter"
        :label-text (tr [:gis-server-config/system])
        :values     (cons :all gis-config-model/system-types)
        :value      system-filter
        :on-change  #(>evt [::set-system-filter %])
        :val->str   #(tr [(keyword "gis-server-config" %)])}]
      (cc/button {:icon     i/plus
                  :on-click #(>evt [::set-server-editor-value
                                    :new
                                    {:gis-server-config/system           :arcgis
                                     :gis-server-config/arcgis-auth-type :arcgis.authentication/none}])}
                 (tr [:gis-editor/add-server]))]
     (when (<sub [::server-config-in-editor :new])
       [gis-server-editor tr :new])
     [system-server-list tr]]))
